import classNames from 'classnames'
import { omit } from 'lodash'
import { nanoid } from 'nanoid'
import { useEffect, useRef, useState, type ComponentType, forwardRef } from 'react'
import { Scrollbars } from 'react-custom-scrollbars-2'
import { getAriaLabel } from 'shared/lib'
import {
  type IScrollbarsPropsRef,
  useScrollbarApiRef,
} from 'shared/ui/Scrollbar/hooks/useScrollbarApiRef'
import { useMutationObserver } from 'shared/ui/Scrollbar/hooks/useMutationObserver'
import { type IScrollbarProps, type IScrollbarVirtualizationProps } from './types'
import styles from './style.module.scss'

type IScrollbarsWithView = Scrollbars & { view?: HTMLElement }

const VIRTUAL_ONLY_PROPS: (keyof IScrollbarVirtualizationProps)[] = [
  'virtualized',
  'virtualListRef',
  'estimateSize',
  'overscan',
  'gap',
  'paddingStart',
  'paddingEnd',
  'onEndReached',
  'endReachedThreshold',
  'onStartReached',
  'startReachedThreshold',
  'anchorTo',
  'followOnAppend',
  'scrollEndThreshold',
  'directDomUpdates',
  'useAnimationFrameWithResizeObserver',
  'header',
  'footer',
  'contentWrapperClassName',
  'contentClassName',
  'measureItems',
  'flushSyncOnScroll',
  'skeletonWhileScrolling',
  'renderSkeleton',
]

export const PlainScrollbar = forwardRef<IScrollbarsPropsRef, IScrollbarProps>(
  (
    {
      children,
      hideTrack,
      scrollToTOpTrigger,
      viewClassName,
      trackVerticalClassName,
      scrollToBottomTrigger,
      indentX = 0,
      onViewRef,
      ...rest
    },
    ref
  ) => {
    const props = omit(rest, VIRTUAL_ONLY_PROPS)
    const scrollbarRef = useRef<Scrollbars>(null)
    const [show, setShow] = useState(true)
    const callbackMouseover = () => {
      setShow(false)
    }
    const callbackMouseout = () => {
      setShow(true)
    }

    useScrollbarApiRef(ref, scrollbarRef)
    useMutationObserver(scrollbarRef)

    useEffect(() => {
      onViewRef?.((scrollbarRef.current as IScrollbarsWithView | null)?.view ?? null)

      return () => onViewRef?.(null)
    }, [])

    useEffect(() => {
      if (scrollbarRef.current) {
        scrollbarRef.current.container.addEventListener('mouseover', callbackMouseover)
        scrollbarRef.current.container.addEventListener('mouseout', callbackMouseout)

        scrollbarRef.current.container.style.setProperty('--indent', String(indentX))
      }

      return () => {
        if (scrollbarRef.current) {
          scrollbarRef.current.container.removeEventListener('mouseover', callbackMouseover)
          scrollbarRef.current.container.removeEventListener('mouseout', callbackMouseout)
        }
      }
    }, [])

    useEffect(() => {
      if (scrollbarRef.current) {
        if (scrollToTOpTrigger) {
          scrollbarRef.current.scrollToTop()
        }
      }
    }, [scrollToTOpTrigger])

    useEffect(() => {
      if (scrollbarRef.current) {
        if (scrollToBottomTrigger) {
          scrollbarRef.current.scrollToBottom()
        }
      }
    }, [scrollToBottomTrigger])

    const renderView = (props: ComponentType) => {
      return (
        <div
          {...props}
          className={classNames(styles.view, viewClassName, `scrollbarView-${nanoid(2)}`)}
          aria-label={getAriaLabel('Scrollbar', 'view')}
        />
      )
    }

    const renderTrackHorizontal = (props: ComponentType) => {
      return (
        <div
          {...props}
          aria-label={getAriaLabel('Scrollbar', 'trackHorizontal')}
          className={classNames(styles.trackHorizontal, {
            [styles.hiddenTrack]: hideTrack,
          })}
        />
      )
    }

    const renderThumbHorizontal = (props: ComponentType) => {
      return (
        <div
          {...props}
          aria-label={getAriaLabel('Scrollbar', 'ThumbHorizontal')}
          className={styles.thumbHorizontal}
        />
      )
    }

    const renderTrackVertical = (props: ComponentType) => {
      return (
        <div
          {...props}
          aria-label={getAriaLabel('Scrollbar', 'TrackVertical')}
          className={classNames(styles.trackVertical, trackVerticalClassName, {
            [styles.hiddenTrack]: hideTrack,
          })}
        />
      )
    }

    const renderThumbVertical = (props: ComponentType) => {
      return (
        <div
          {...props}
          aria-label={getAriaLabel('Scrollbar', 'ThumbVertical')}
          className={styles.thumbVertical}
        />
      )
    }

    return (
      <Scrollbars
        ref={scrollbarRef}
        autoHide={show}
        autoHideTimeout={300}
        autoHideDuration={200}
        style={{ width: '100%', height: props.height || '100%' }}
        renderView={renderView}
        renderTrackHorizontal={renderTrackHorizontal}
        renderThumbHorizontal={renderThumbHorizontal}
        renderTrackVertical={renderTrackVertical}
        renderThumbVertical={renderThumbVertical}
        aria-label={getAriaLabel('Scrollbar')}
        {...props}
        className={classNames(styles.scrollbars, props.className)}
      >
        {children}
      </Scrollbars>
    )
  }
)

PlainScrollbar.displayName = 'PlainScrollbar'
